import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { Reveal, LineReveal, Chapter, ParallaxImage } from "../components/motion";
import FAQList from "../components/FAQList";
import CTABand from "../components/CTABand";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSEO, usePageView } from "../lib/seo";

const INCLUDED = [
  "A comprehensive assessment of your child's sleep diary and current rhythm",
  "A gentle, practical plan tailored to your child's age, temperament and your family",
  "Defined follow-up support — so you're never implementing alone",
  "Honest guidance about what's age-appropriate and realistic",
  "Referral to your GP or child health nurse whenever something sits outside sleep",
];

const NOT_INCLUDED = [
  "Cry-it-out or any method that asks you to ignore your child",
  "Medical advice, diagnosis or treatment — that's your GP's role, always",
  "Guaranteed outcomes — every child is different and no ethical consultant can promise one",
  "Overnight in-home stays (my support is via consultations, plans and message support)",
  "A rigid, one-size-fits-all schedule your family has to survive",
];

export default function HowIWork() {
  const [faqs, setFaqs] = useState([]);
  useSEO({
    title: "How I Work — Gentle, Evidence-Informed Sleep Consulting",
    description: "My approach: gentle, responsive and tailored. What's included, what isn't, my credentials, and honest answers to the questions parents ask.",
  });
  usePageView("/how-i-work");

  useEffect(() => {
    api.faqs().then(setFaqs).catch(() => {});
  }, []);

  return (
    <div data-testid="how-i-work-page">
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="overline-label mb-5">How I work</p>
          <LineReveal
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-ink"
            lines={["Gentle isn't a", "marketing word here.", "It's the whole method."]}
          />
          <Reveal delay={0.5}>
            <p className="mt-7 text-lg text-ink-soft leading-relaxed">
              Here's exactly what working together looks like — the principles, the boundaries, and what you can expect from me.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-16">
          <Chapter number="01" title="Gentle and responsive, always">
            <p>I don't use cry-it-out or any method that asks you to leave your little one to cry alone. We build healthy sleep habits step by step, in a way that protects your bond and actually feels sustainable for your family.</p>
          </Chapter>
          <Chapter number="02" title="Evidence-informed, never one-size-fits-all">
            <p>Sleep guidance here is grounded in what we know about infant sleep development, circadian rhythms and attachment — then adapted to your child's temperament and your family's real life. A plan only works if you can actually live it.</p>
          </Chapter>
          <Chapter number="03" title="Your family's values lead">
            <p>Co-sleeping, contact naps, feeding to sleep — nothing is 'wrong' if it works for you. We only change what's exhausting you, and we change it at a pace your child (and you) can handle.</p>
          </Chapter>
          <Chapter number="04" title="Clear boundaries keep you safe">
            <p>Sleep consulting is behavioural and routine-based guidance — not medical care. If anything suggests a health issue, I'll always point you to your GP or child health nurse first. And I won't promise outcomes no one can honestly guarantee.</p>
          </Chapter>
        </div>
      </section>

      <section className="py-20 bg-warm/70 border-y border-ink/[0.05]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <ParallaxImage
              src="https://images.squarespace-cdn.com/content/v1/68b7e04ed5afe32710b42d83/ca8dc117-2d44-4cdc-b7c4-6730cf500e7b/IMG_5785.jpg"
              alt="Tamika working with a family"
              className="rounded-[2rem] aspect-[4/3] shadow-[0_20px_50px_rgba(44,40,37,0.12)]"
            />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="overline-label mb-4">Credentials</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-ink leading-tight mb-6">Trained, certified, and battle-tested by real bedtimes</h2>
            <p className="text-ink-soft leading-relaxed mb-4">
              I'm a certified infant and child sleep consultant, and before that I spent over six years as a professional nanny — caring for children of all ages and supporting families through milestones, regressions and very long nights.
            </p>
            <p className="text-ink-soft leading-relaxed">
              That combination — formal training plus years of hands-on experience — means my advice is practical, not theoretical. I've sat on a lot of nursery floors.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-12 max-w-xl">
            <p className="overline-label mb-4">Expectations, honestly</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-ink leading-tight">What's included — and what isn't</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="card-soft p-8 h-full !bg-sage-light/50 border-sage/20">
                <h3 className="font-serif text-xl font-medium text-ink mb-6">Working together includes</h3>
                <ul className="space-y-3.5">
                  {INCLUDED.map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm text-ink-soft leading-relaxed">
                      <Check className="w-4.5 h-4.5 w-[18px] h-[18px] text-sage shrink-0 mt-0.5" strokeWidth={2.5} />{item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="card-soft p-8 h-full">
                <h3 className="font-serif text-xl font-medium text-ink mb-6">What I don't do</h3>
                <ul className="space-y-3.5">
                  {NOT_INCLUDED.map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm text-ink-soft leading-relaxed">
                      <X className="w-[18px] h-[18px] text-terra shrink-0 mt-0.5" strokeWidth={2.5} />{item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24 sm:pb-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal className="mb-10">
            <p className="overline-label mb-4">FAQs</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-medium text-ink">The questions every parent asks</h2>
          </Reveal>
          <FAQList faqs={faqs} testid="how-i-work-faq" />
          <Reveal className="mt-10 text-center">
            <Link to="/support-options" data-testid="how-i-work-options-cta" className="btn-primary">See the support options</Link>
          </Reveal>
        </div>
      </section>

      <CTABand title="Sound like your kind of support?" />
    </div>
  );
}
